'use client';

import { invariant } from '@ifixit/helpers';
import { useBodyScrollLock } from '@ifixit/ui/hooks/use-body-scroll-lock';
import { useControllableState } from '@ifixit/ui/hooks/use-controllable-state';
import { useFocusTrap } from '@ifixit/ui/hooks/use-focus-trap';
import { useKeyDown } from '@ifixit/ui/hooks/use-key-down';
import { useMounted } from '@ifixit/ui/hooks/use-mounted';
import { cn } from '@ifixit/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import {
   createContext,
   useContext,
   useId,
   useRef,
   type ComponentProps,
   type ReactNode,
   type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

interface SheetContextValue {
   open: boolean;
   setOpen: (open: boolean) => void;
   contentId: string;
   titleId: string;
   descriptionId: string;
   closeButtonRef: RefObject<HTMLButtonElement | null>;
}

const SheetContext = createContext<SheetContextValue | null>(null);

function useSheetContext() {
   const context = useContext(SheetContext);
   invariant(context, 'Sheet components must be used within a Sheet');
   return context;
}

export function useSheet() {
   const context = useSheetContext();
   return {
      isOpen: context.open,
      open: () => context.setOpen(true),
      close: () => context.setOpen(false),
      toggle: () => context.setOpen(!context.open),
   };
}

interface SheetProps {
   children: ReactNode;
   open?: boolean;
   onOpenChange?: (open: boolean) => void;
   defaultOpen?: boolean;
}

function Sheet({ children, open, onOpenChange, defaultOpen = false }: SheetProps) {
   const [openState, setOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
   });

   const id = useId();
   const contentId = `${id}-content`;
   const titleId = `${id}-title`;
   const descriptionId = `${id}-description`;
   const closeButtonRef = useRef<HTMLButtonElement>(null);

   return (
      <SheetContext.Provider
         value={{ open: openState, setOpen, contentId, titleId, descriptionId, closeButtonRef }}
      >
         {children}
      </SheetContext.Provider>
   );
}

interface SheetTriggerProps extends ComponentProps<'button'> {
   asChild?: boolean;
}

function SheetTrigger({ onClick, asChild = false, ...props }: SheetTriggerProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const Comp = asChild ? Slot : 'button';

   return (
      <Comp
         data-slot="sheet-trigger"
         aria-haspopup="dialog"
         aria-expanded={sheet.isOpen}
         aria-controls={context.contentId}
         onClick={event => {
            onClick?.(event);
            sheet.open();
         }}
         {...props}
      />
   );
}

interface SheetCloseProps extends ComponentProps<'button'> {
   asChild?: boolean;
}

function SheetClose({ onClick, asChild = false, ...props }: SheetCloseProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const Comp = asChild ? Slot : 'button';

   return (
      <Comp
         // eslint-disable-next-line react-hooks/refs
         ref={context.closeButtonRef}
         data-slot="sheet-close"
         onClick={e => {
            onClick?.(e);
            sheet.close();
         }}
         {...props}
      />
   );
}

interface SheetPortalProps {
   children: ReactNode;
   container?: HTMLElement | null;
}

function SheetPortal({ children, container }: SheetPortalProps) {
   const mounted = useMounted();

   if (!mounted) return null;

   return createPortal(children, container || document.body);
}

function SheetOverlay({ className, onClick, ...props }: ComponentProps<'div'>) {
   const { isOpen, close } = useSheet();

   return (
      <div
         data-slot="sheet-overlay"
         data-state={isOpen ? 'open' : 'closed'}
         className={cn(
            'fixed inset-0 z-[150] bg-black/50 transition-[opacity,visibility] duration-300',
            'data-[state=closed]:opacity-0 data-[state=closed]:invisible data-[state=closed]:pointer-events-none data-[state=open]:opacity-100',
            className
         )}
         onClick={e => {
            onClick?.(e);
            close();
         }}
         {...props}
      />
   );
}

interface SheetContentProps extends ComponentProps<'div'> {
   side?: 'top' | 'right' | 'bottom' | 'left';
}

function SheetContent({ className, children, side = 'right', ...props }: SheetContentProps) {
   const context = useSheetContext();
   const sheet = useSheet();
   const contentRef = useFocusTrap<HTMLDivElement>({
      enabled: sheet.isOpen,
      initialFocusRef: context.closeButtonRef,
   });

   useKeyDown(
      event => {
         if (event.key === 'Escape') sheet.close();
      },
      { enabled: sheet.isOpen }
   );

   useBodyScrollLock(sheet.isOpen);

   return (
      <SheetPortal>
         <SheetOverlay />
         <div
            ref={contentRef}
            id={context.contentId}
            role="dialog"
            aria-modal="true"
            aria-labelledby={context.titleId}
            aria-describedby={context.descriptionId}
            tabIndex={-1}
            data-slot="sheet-content"
            data-state={sheet.isOpen ? 'open' : 'closed'}
            className={cn(
               // Hide with `visibility` in addition to `translate`: browsers
               // without `translate` support would otherwise show the closed
               // sheet, and off-screen content stays in the tab order.
               // Transitioning visibility delays the flip until the slide ends.
               'bg-background fixed z-[150] flex flex-col gap-4 shadow-lg transition-[translate,visibility] duration-300 ease-in-out data-[state=closed]:invisible',
               side === 'right' &&
                  'inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm data-[state=closed]:translate-x-full data-[state=open]:translate-x-0',
               side === 'left' &&
                  'inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm data-[state=closed]:-translate-x-full data-[state=open]:translate-x-0',
               side === 'top' &&
                  'inset-x-0 top-0 h-auto border-b data-[state=closed]:-translate-y-full data-[state=open]:translate-y-0',
               side === 'bottom' &&
                  'inset-x-0 bottom-0 h-auto border-t data-[state=closed]:translate-y-full data-[state=open]:translate-y-0',
               className
            )}
            {...props}
         >
            {children}
         </div>
      </SheetPortal>
   );
}

function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="sheet-header"
         className={cn('flex flex-col gap-1.5 p-4', className)}
         {...props}
      />
   );
}

function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
   return (
      <div
         data-slot="sheet-footer"
         className={cn('mt-auto flex flex-col gap-2 p-4', className)}
         {...props}
      />
   );
}

interface SheetTitleProps extends ComponentProps<'h2'> {
   asChild?: boolean;
}

function SheetTitle({ className, asChild = false, ...props }: SheetTitleProps) {
   const context = useSheetContext();
   const Comp = asChild ? Slot : 'h2';
   return (
      <Comp
         id={context.titleId}
         data-slot="sheet-title"
         className={cn('text-foreground font-semibold', className)}
         {...props}
      />
   );
}

interface SheetDescriptionProps extends ComponentProps<'p'> {
   asChild?: boolean;
}

function SheetDescription({ className, asChild = false, ...props }: SheetDescriptionProps) {
   const context = useSheetContext();
   const Comp = asChild ? Slot : 'p';
   return (
      <Comp
         id={context.descriptionId}
         data-slot="sheet-description"
         className={cn('text-muted-foreground text-sm', className)}
         {...props}
      />
   );
}

export {
   Sheet,
   SheetClose,
   SheetContent,
   SheetDescription,
   SheetFooter,
   SheetHeader,
   SheetTitle,
   SheetTrigger,
};
